import { Link } from 'react-router-dom';
import { useVisibleBrands } from '@/hooks/useSaleEvent';
import type { SaleParticipant } from '@/seo/saleEvents';
import { formatLocalDayRange } from '@/seo/text';
import { entityPath } from '@/seo/urls';

/** "Up to 50% off" from the owner's structured percentage only (1–100). */
function discountText(max: number | undefined): string | null {
  if (typeof max !== 'number' || !Number.isFinite(max)) return null;
  const value = Math.round(max);
  return value > 0 && value <= 100 ? `Up to ${value}% off` : null;
}

/**
 * "Brands that ran a sale last year": the owner-compiled list for the event
 * (`lastYearParticipants`, OWNER CONTENT in @/seo/saleEvents).
 *
 * - A brand listed without an id is shown as plain text.
 * - A brand listed with an id is shown only once the backend confirms it is
 *   publicly visible, and then it links to its page at the backend's
 *   canonical `urlPath` (the id URL only when there is none). The public API answers
 *   the same NOT_FOUND for a blocked brand as for a draft or a brand outside
 *   the country, so anything it does not confirm is left out: a blocked brand
 *   is never named, not even while the answer is loading.
 * - With nothing to show (an empty list, or no brand confirmed) the whole
 *   section, heading included, is not rendered.
 */
export function LastYearBrands({ participants }: { participants: readonly SaleParticipant[] }) {
  const ids = participants.map((p) => p.brandId).filter((id): id is string => !!id);
  const visible = useVisibleBrands(ids);

  const rows = participants.filter((p) => !p.brandId || visible.has(p.brandId));
  if (rows.length === 0) return null;

  return (
    <section aria-labelledby="last-year-brands-heading">
      <h2 id="last-year-brands-heading" className="text-xl font-extrabold tracking-tight text-ink">
        Brands that ran a sale last year
      </h2>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((p) => {
          const dates = p.startDate ? formatLocalDayRange(p.startDate, p.endDate) : '';
          const discount = discountText(p.maxDiscount);
          const details = [dates, discount].filter(Boolean).join(' · ');
          const brand = p.brandId ? visible.get(p.brandId) : undefined;
          return (
            <li
              key={p.brandId ?? p.brandName}
              className="rounded-xl border border-card-border bg-card px-4 py-3"
            >
              {brand ? (
                <Link
                  to={entityPath(brand, 'brand')}
                  className="font-bold text-ink transition-colors duration-200 hover:text-primary-strong"
                >
                  {p.brandName}
                </Link>
              ) : (
                <span className="font-bold text-ink">{p.brandName}</span>
              )}
              {details && <p className="mt-0.5 text-sm text-ink-secondary">{details}</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default LastYearBrands;
